import React, { useEffect, useRef } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'

interface CustomQuillProps {
    onDescriptionChange: (value: string) => void
    emailText: string
}

const CustomQuill: React.FC<CustomQuillProps> = ({
    onDescriptionChange,
    emailText,
}) => {
    const toolbarRef = useRef<HTMLDivElement>(null)

    const onChange = (value: string): void => {
        onDescriptionChange(value)
    }

    useEffect(() => {
        const toolbarButtons =
            toolbarRef.current?.querySelectorAll<HTMLElement>(
                '.ql-toolbar button, .ql-toolbar .ql-picker'
            )

        toolbarButtons?.forEach((element) => {
            if (element instanceof HTMLButtonElement) {
                const className = element.className
                if (className.includes('ql-bold')) {
                    element.setAttribute('data-tooltip-id', 'tooltip-bold')
                    element.setAttribute(
                        'data-tooltip-content',
                        'Bold (Ctrl+B)'
                    )
                } else if (className.includes('ql-italic')) {
                    element.setAttribute('data-tooltip-id', 'tooltip-italic')
                    element.setAttribute(
                        'data-tooltip-content',
                        'Italic (Ctrl+I)'
                    )
                } else if (className.includes('ql-underline')) {
                    element.setAttribute('data-tooltip-id', 'tooltip-underline')
                    element.setAttribute(
                        'data-tooltip-content',
                        'Underline (Ctrl+U)'
                    )
                } else if (className.includes('ql-link')) {
                    element.setAttribute('data-tooltip-id', 'tooltip-link')
                    element.setAttribute(
                        'data-tooltip-content',
                        'Insert Link (Ctrl+K)'
                    )
                } else if (className.includes('ql-strike')) {
                    element.setAttribute('data-tooltip-id', 'tooltip-strike')
                    element.setAttribute(
                        'data-tooltip-content',
                        'Strikethrough'
                    )
                } else if (className.includes('ql-clean')) {
                    element.setAttribute('data-tooltip-id', 'tooltip-clean')
                    element.setAttribute(
                        'data-tooltip-content',
                        'Clear Formatting'
                    )
                } else if (
                    className.includes('ql-script') &&
                    element.value === 'sub'
                ) {
                    element.setAttribute('data-tooltip-id', 'tooltip-subscript')
                    element.setAttribute('data-tooltip-content', 'Subscript')
                } else if (
                    className.includes('ql-script') &&
                    element.value === 'super'
                ) {
                    element.setAttribute(
                        'data-tooltip-id',
                        'tooltip-superscript'
                    )
                    element.setAttribute('data-tooltip-content', 'Superscript')
                } else if (className.includes('ql-list')) {
                    const type =
                        element.value === 'ordered'
                            ? 'Ordered List (Ctrl+Shift+7)'
                            : 'Bullet List (Ctrl+Shift+8)'
                    element.setAttribute(
                        'data-tooltip-id',
                        `tooltip-${element.value}-list`
                    )
                    element.setAttribute('data-tooltip-content', type)
                } else if (className.includes('ql-image')) {
                    element.setAttribute('data-tooltip-id', 'tooltip-image')
                    element.setAttribute('data-tooltip-content', 'Insert Image')
                } else if (className.includes('ql-video')) {
                    element.setAttribute('data-tooltip-id', 'tooltip-video')
                    element.setAttribute('data-tooltip-content', 'Insert Video')
                } else if (className.includes('ql-code-block')) {
                    element.setAttribute('data-tooltip-id', 'tooltip-code')
                    element.setAttribute('data-tooltip-content', 'Code View')
                }
            } else if (element.classList.contains('ql-header')) {
                const pickerLabel = element.querySelector('.ql-picker-label')
                if (pickerLabel !== null) {
                    pickerLabel.setAttribute(
                        'data-tooltip-id',
                        'tooltip-header'
                    )
                    pickerLabel.setAttribute(
                        'data-tooltip-content',
                        'Heading (Ctrl+Alt+H)'
                    )
                }
            }
        })
    }, [])

    const modules = {
        toolbar: [
            [{ font: [] }, { size: [] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ script: 'sub' }, { script: 'super' }],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }],
            ['link', 'image', 'video'],
            ['clean', 'code-block'],
        ],
    }

    const formats = [
        'font',
        'size',
        'bold',
        'italic',
        'underline',
        'strike',
        'script',
        'color',
        'background',
        'list',
        'bullet',
        'align',
        'link',
        'image',
        'video',
        'clean',
        'code-block',
        'lineHeight',
        'header',
    ]
    // const modules = {
    //     toolbar: [
    //         [{ header: [1, 2, 3, 4, 5, 6, false] }],
    //         ['bold', 'italic', 'underline', 'strike'],
    //         ['blockquote', 'code-block'],
    //         [{ list: 'ordered' }, { list: 'bullet' }],
    //         [{ script: 'sub' }, { script: 'super' }],
    //         [{ indent: '-1' }, { indent: '+1' }],
    //         [{ color: [] }, { background: [] }],
    //         [{ align: [] }],
    //         ['link', 'image', 'video', 'formula'],
    //         ['clean'],
    //     ],
    //     clipboard: {
    //         matchVisual: false,
    //     },
    // }

    // const formats = [
    //     'header',
    //     'bold',
    //     'italic',
    //     'underline',
    //     'strike',
    //     'blockquote',
    //     'code-block',
    //     'list',
    //     'bullet',
    //     'script',
    //     'indent',
    //     'size',
    //     'color',
    //     'background',
    //     'align',
    //     'link',
    //     'image',
    //     'video',
    //     'formula',
    // ]

    return (
        <div ref={toolbarRef}>
            <ReactQuill
                theme="snow"
                value={emailText}
                onChange={onChange}
                modules={modules}
                formats={formats}
                placeholder="Start typing your content here..."
            />
            <Tooltip id="tooltip-bold" />
            <Tooltip id="tooltip-italic" />
            <Tooltip id="tooltip-underline" />
            <Tooltip id="tooltip-strike" />
            <Tooltip id="tooltip-subscript" />
            <Tooltip id="tooltip-superscript" />
            <Tooltip id="tooltip-link" />
            <Tooltip id="tooltip-image" />
            <Tooltip id="tooltip-video" />
            <Tooltip id="tooltip-clean" />
            <Tooltip id="tooltip-code" />
            <Tooltip id="tooltip-header" />
            <Tooltip id="tooltip-ordered-list" />
            <Tooltip id="tooltip-bullet-list" />
        </div>
    )
}

export default CustomQuill
